import { Paper, Grid } from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { acknowledgeNotification, acknowledgeNotificationDTO, getNotification } from 'wisestep-api';
import {
  WsTypography,
  WsTypoVariants,
  WsTypoColors,
  TeamIcon,
  TaskIcon,
  CandidateIconAlt,
  SmtpSetupConfigIcon,
} from 'wisestep-base';

import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { DataType } from '../types/NotificationDTO';

export interface NotificationListingType {
  status: 'all' | 'read' | 'unread';
}
const NotificationListing: FC<NotificationListingType> = ({ status = 'all' }) => {
  const typeArray = ['NEW_EMAIL', 'OVERDUE_TASK'];
  const workspaceContext = useWorkspaceContext();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [notificationsWithKeyedData, setNotificationsWithKeyedData] =
    useState<acknowledgeNotificationDTO[]>();
  const [notificationsRes, setNotificationsRes] = useState<acknowledgeNotificationDTO[]>();

  useEffect(() => {
    const asyncRequest = async () => {
      let notificationResponse;
      if (status === 'all') {
        notificationResponse = await getNotification({
          page: 0,
          size: 10,
          sort: 'createdOn,desc',
        });
      } else {
        notificationResponse = await getNotification({
          page: 0,
          size: 10,
          sort: 'createdOn,desc',
          status: status,
        });
      }
      const notifications = notificationResponse.data
        ? (notificationResponse.data as acknowledgeNotificationDTO[]).filter(
            (single) => single.status !== 'ACKNOWLEDGED'
          )
        : undefined;
      if (notifications) {
        const payload: acknowledgeNotificationDTO[] = [];
        notifications.forEach((single: acknowledgeNotificationDTO) => {
          const singleNotification = single;
          singleNotification.keyedData = {};
          singleNotification.data.forEach((_single, index) => {
            singleNotification.keyedData[singleNotification.data[index].key] =
              singleNotification.data[index].value;
          });
          payload.push(singleNotification);
        });
        setNotificationsWithKeyedData(payload);
        setNotificationsRes(notifications);
      }
    };
    asyncRequest();
  }, [status]);

  const acknowledgeNotifications = async (index: number) => {
    if (notificationsRes) {
      const responseArr: acknowledgeNotificationDTO[] = notificationsRes;
      const singleNotification = responseArr[index];
      singleNotification.keyedData = {};
      singleNotification.data.forEach((_single, index) => {
        singleNotification.keyedData[singleNotification.data[index].key] =
          singleNotification.data[index].value;
      });
      try {
        if (workspaceContext.link) {
          window.open(
            NotificationHandler(
              workspaceContext.link,
              getValue(singleNotification.data, 'jobId'),
              singleNotification.type
            ),
            '_blank'
          );
          await acknowledgeNotification(singleNotification);
        }
      } catch (error) {
        setNotification(warning('Something Went Wrong , Please try again'));
      }
    }
  };
  const NotificationTitle = (notificationType: string, index: number) => {
    let title = '';
    const notificationTypes = {
      ['OVERDUE_TASK']: () => {
        if (
          (notificationsWithKeyedData &&
            (notificationsWithKeyedData[index].keyedData['title'] ||
              notificationsWithKeyedData[index].keyedData['name'])) != 'unknown'
        ) {
          title = ((notificationsWithKeyedData &&
            (notificationsWithKeyedData[index].keyedData['title'] ||
              notificationsWithKeyedData[index].keyedData['name'])) ??
            '') as string;
        }
      },
      ['CANDIDATE_SUBMISSION']: () => {
        title = `New Submission`;
      },
      ['STAGE_STATUS_CHANGED']: () => {
        title = `Updated Submission`;
      },
      ['NOTE_ADDED']: () => {
        title = `Updated Submission`;
      },
      ['CLOSE_JOB']: () => {
        title = `Job Closed`;
      },
      ['CANDIDATE_APPLICATION']: () => {
        title = `New Application`;
      },
      ['JOB_ASSIGNMENT']: () => {
        title = ` Job Assigned`;
      },
      ['HARVESTING']: () => {
        title =
          (notificationsWithKeyedData &&
            `${notificationsWithKeyedData[index].keyedData['harvestName']} processing status`) ??
          '';
      },
      ['INBOUND_SMS']: () => {
        title =
          (notificationsWithKeyedData &&
            `New message from ${notificationsWithKeyedData[index].keyedData['from']}`) ??
          '';
      },
      ['NEW_EMAIL']: () => {
        title = (notificationsWithKeyedData && notificationsWithKeyedData[index].message) ?? '';
      },
      ['RTR_RESPONSE']: () => {
        title = 'RTR Response Received';
      },
      ['INFORMATION_RESPONSE']: () => {
        title = 'Information Request Response Received';
      },
      ['EVALUATION_RESPONSE']: () => {
        title = 'Evaluation Response Received';
      },
    };
    Object.keys(notificationTypes).includes(notificationType) &&
      notificationTypes[notificationType]();
    return title;
  };
  const dynamicIcon = (notificationType: string) => {
    let icon = <TaskIcon width={50} height={50} />;
    const iconHandle = {
      ['OVERDUE_TASK']: () => {
        icon = <TaskIcon width={50} height={50} />;
      },
      ['CANDIDATE_SUBMISSION']: () => {
        icon = <CandidateIconAlt />;
      },
      ['STAGE_STATUS_CHANGED']: () => {
        icon = <CandidateIconAlt />;
      },
      ['NOTE_ADDED']: () => {
        icon = <CandidateIconAlt />;
      },
      ['CLOSE_JOB']: () => {
        icon = <TeamIcon width={50} height={50} />;
      },
      ['CANDIDATE_APPLICATION']: () => {
        icon = <CandidateIconAlt />;
      },
      ['JOB_ASSIGNMENT']: () => {
        icon = <TeamIcon width={50} height={50} />;
      },
      ['HARVESTING']: () => {
        icon = <TeamIcon width={50} height={50} />;
      },
      ['INBOUND_SMS']: () => {
        icon = <TeamIcon width={50} height={50} />;
      },
      ['NEW_EMAIL']: () => {
        icon = <SmtpSetupConfigIcon />;
      },
      ['RTR_RESPONSE']: () => {
        icon = <CandidateIconAlt />;
      },
      ['INFORMATION_RESPONSE']: () => {
        icon = <CandidateIconAlt />;
      },
      ['EVALUATION_RESPONSE']: () => {
        icon = <CandidateIconAlt />;
      },
    };
    Object.keys(iconHandle).includes(notificationType) && iconHandle[notificationType]();
    return icon;
  };
  const NotificationHandler = (workspaceName: string, jobId: string, type: string) => {
    let applicationLink = '';

    const responseHandler = {
      ['CANDIDATE_APPLICATION']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/2/pipeline`;
      },
      ['CANDIDATE_SUBMISSION']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/1/pipeline`;
      },
      ['NOTE_ADDED']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/1/pipeline`;
      },
      ['STAGE_STATUS_CHANGED']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/1/pipeline`;
      },
      ['OVERDUE_TASK']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/tasks`;
      },
      ['CLOSE_JOB']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/0/pipeline`;
      },
      ['JOB_ASSIGNMENT']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/jobs-crm/${jobId}/false/0/pipeline`;
      },
      ['HARVESTING']: () => {
        applicationLink = '';
      },
      ['INBOUND_SMS']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/sms-logs`;
      },
      ['NEW_EMAIL']: () => {
        applicationLink = `https://${workspaceName}.wisestep.com/email-settings/logs/inbox/`;
      },
      ['RTR_RESPONSE']: () => {
        applicationLink = '';
      },
      ['INFORMATION_RESPONSE']: () => {
        applicationLink = '';
      },
      ['EVALUATION_RESPONSE']: () => {
        applicationLink = '';
      },
    };
    Object.keys(responseHandler).includes(type) && responseHandler[type]();
    return applicationLink;
  };
  const getValue = (notification: DataType[], key: string) => {
    let value = '';
    notification.map((single) => {
      if (single.key === key) value = single.value;
    });
    return value;
  };
  return (
    <>
      {notificationsRes &&
        notificationsRes.map((single, index) => (
          <Paper
            key={index}
            elevation={1}
            onClick={() => acknowledgeNotifications(index)}
            sx={{ margin: 1, display: 'flex' }}
          >
            <Grid container p={3} width={600} height={150} direction="row" display="flex">
              <Grid mt={2} item xs={2} display="flex">
                {dynamicIcon(single.type)}
              </Grid>
              <Grid item xs={10} container direction="column" display="flex">
                <Grid item xs={6}>
                  <WsTypography onClick={() => {}} variant={WsTypoVariants.BODY1}>
                    {single.type != 'NEW_EMAIL' && NotificationTitle(single.type, index)}
                  </WsTypography>
                </Grid>
                <Grid item xs={6}>
                  <WsTypography variant={WsTypoVariants.BODY2} color={WsTypoColors.TERTIARY}>
                    {notificationsWithKeyedData &&
                      (typeArray.indexOf(single.type) === -1
                        ? `${notificationsWithKeyedData[index].keyedData['title']}`
                        : single.type === 'OVERDUE_TASK'
                        ? 'You have a task!'
                        : NotificationTitle(single.type, index))}
                  </WsTypography>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        ))}
    </>
  );
};
export default NotificationListing;
